import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import { GfImage, GfImageTag } from "@swagger/typescript-fetch-goflyer";
import { useImageTagOptions } from "../../../hooks";
import { adminApiService } from "../../../utils/api";
import { CustomAutocompleteMultiSelect } from "../CustomAutocomplete";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import { toastError, toastSuccess } from "../../../utils/pageUtil/toast";

interface Props {
  open: boolean;
  image: GfImage | null;
  onClose: () => void;
  onSaved?: (updatedImage: GfImage) => void;
}

export const ImageTagEditModal: React.FC<Props> = ({
  open,
  image,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const { imageTagOptionList } = useImageTagOptions();
  const [selectedTags, setSelectedTags] = useState<GfImageTag[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (image) {
      setSelectedTags(image.imageTags || []);
    }
  }, [image]);

  const handleSave = async () => {
    if (!image?.id) return;
    setIsSaving(true);
    try {
      const updated =
        await adminApiService.updateOneBaseGfImageAdminControllerGfImage(
          { imageTags: selectedTags },
          image.id,
        );
      toastSuccess(t(messages.Successfully_Updated()));
      onSaved?.(updated);
      onClose();
    } catch (error) {
      console.error("Error updating image tags:", error);
      toastError(t(messages.There_is_an_error_please_Contact_Administrator()));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      sx={{ zIndex: 1600 }}
      PaperProps={{ sx: { minHeight: 420, overflow: "visible" } }}
      data-testid="image-tag-edit-modal"
    >
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}
      >
        <LocalOfferIcon sx={{ color: "#f58220" }} />
        <Typography variant="h6" component="span">
          {t(messages.Image_Tag())}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ overflow: "visible" }}>
        {image?.src && (
          <Box textAlign="center" mb={2}>
            <img
              src={image.srcSmall || image.src}
              alt={image.alt || ""}
              style={{
                maxWidth: "100%",
                maxHeight: 160,
                borderRadius: 6,
                border: "1px solid #eee",
                objectFit: "contain",
              }}
              data-testid="image-tag-edit-preview"
            />
            {image.alt && (
              <Typography
                variant="caption"
                display="block"
                mt={0.5}
                color="text.secondary"
              >
                {image.alt}
              </Typography>
            )}
          </Box>
        )}

        <CustomAutocompleteMultiSelect
          title={t(messages.Image_Tag())}
          optionsList={imageTagOptionList?.map((tag) => ({
            value: tag,
            label: tag.name,
          }))}
          selectedValues={selectedTags.map((tag) => ({
            value: tag,
            label: tag.name,
          }))}
          multiSelect
          handleOnChange={(value: GfImageTag[] | undefined) =>
            setSelectedTags(value || [])
          }
          WrapperCss="width: 100%; position: relative;"
          inputCss="width: 100%;"
          dataTestId="image-tag-edit-select"
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ borderColor: "#ccc", color: "#555" }}
        >
          {t(messages.Cancel())}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSaving}
          startIcon={
            isSaving ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
          sx={{
            backgroundColor: "#f58220",
            "&:hover": { backgroundColor: "#e4711f" },
          }}
          data-testid="image-tag-edit-save-button"
        >
          {t(messages.Update())}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageTagEditModal;
